import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

const DIMS = [
  { dim: "label", label: "Label", hint: "Tag key = values (e.g. env=prod)" },
  { dim: "sku_category", label: "SKU category", hint: "e.g. Storage, Network" },
  { dim: "service", label: "Service", hint: "e.g. Compute Engine, BigQuery" },
];

const AddRefinementMenu = ({ usedDims, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const offered = DIMS.filter((d) => !usedDims.includes(d.dim));

  // Anchor the menu to the trigger's viewport rect. position: fixed lets the menu escape
  // the surrounding card / scroll container without inheriting their clipping. minWidth
  // comes from the trigger (with a 14rem floor) so the menu sits naturally under the
  // trigger; the actual width grows to fit the widest item content via flex layout.
  const placeMenu = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => { if (open) placeMenu(); }, [open, placeMenu]);

  // Close on outside-click / scroll / resize / Escape. Scroll-to-close is simpler than
  // re-anchoring on every scroll tick and matches the lightweight popover pattern.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onClose = () => setOpen(false);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (offered.length === 0) return null;

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      style={{ position: "fixed", top: pos.top, left: pos.left, minWidth: Math.max(pos.width, 224) }}
      // flex flex-col stretches every item to the menu's full cross-axis (horizontal)
      // width, so the hover background always reaches the panel edge — even when one item's
      // hint text makes the panel grow beyond its minWidth. w-full on a `block` button
      // inside a shrink-to-fit parent can leave a sliver of unhighlighted whitespace on
      // the right; flex stretch behaves predictably regardless of intrinsic widths.
      className="flex flex-col bg-card border border-card-bd rounded-card z-50 py-1 overflow-hidden"
    >
      {offered.map(({ dim, label, hint }) => (
        <button
          key={dim}
          role="menuitem"
          type="button"
          onClick={() => { setOpen(false); onAdd(dim); }}
          className="text-left px-3 py-2 text-sm text-text hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
        >
          <div className="font-medium">{label}</div>
          {hint && <div className="text-[11px] text-text-3 mt-0.5">{hint}</div>}
        </button>
      ))}
    </div>
  );

  return (
    <div className="inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-ctrl border border-border bg-card text-text hover:bg-surface-2 hover:border-text-3"
      >
        <span className="text-text-2">+</span>
        <span>Add refinement</span>
        <span className={`text-text-3 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && createPortal(menu, document.body)}
    </div>
  );
};

AddRefinementMenu.propTypes = {
  usedDims: PropTypes.arrayOf(PropTypes.string).isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default AddRefinementMenu;
